import requestService from 'mithril/request'
import {useReducer, useRef} from 'react'
import {PaginationResult} from 'types'
import {useSkip} from './useskip'

const request = requestService.request

type ListState<T> = {
	page: number
	url: string
	hasMore: boolean
	items: Array<T>
	loading: boolean
}

const listReducer = <T extends {}>(
	state: ListState<T>,
	action
): ListState<T> => {
	switch (action.type) {
		case 'results':
			if (state.url === action.url)
				return {
					...state,
					page: action.results.current_page,
					items: state.items.concat(action.results.data),
					hasMore: action.results.current_page < action.results.last_page,
					loading: false
				}
			return {
				page: action.results.current_page,
				url: action.url,
				items: action.results.data,
				hasMore: action.results.last_page > 1,
				loading: false
			}
		case 'load':
			return {...state, loading: true}
		case 'addVisible':
			return {...state, page: action.page}
		default:
			throw new Error(`No such action: ${action.type}`)
	}
}

export const useList = <T extends {}>({
	hydrate,
	url,
	amounts,
	...req
}: {
	hydrate: PaginationResult<T>
	url: string
	amounts?: {
		start: number
		page: number
	}
	[key: string]: any
}): {
	loading: boolean
	items: Array<T>
	hasMore: boolean
	loadMore: () => Promise<void>
} => {
	const transport = useRef(null)
	const [state, dispatch] = useReducer(listReducer, {
		page: 1,
		url,
		hasMore: hydrate.last_page > 1,
		items: hydrate.data,
		loading: false
	})
	const load = (page: number) => {
		if (transport.current) transport.current.abort()
		dispatch({type: 'load'})
		return request({
			url,
			...req,
			data: {...req.data, page},
			config: xhr => (transport.current = xhr)
		}).then(results => dispatch({type: 'results', url, results}))
	}
	useSkip(() => {
		load(1)
	}, [url])
	const getVisibleAmount = () => {
		if (!amounts) return state.items.length
		return Math.min(
			amounts.start + amounts.page * (state.page - 1),
			state.items.length
		)
	}
	const getItems = () => {
		if (!amounts) return state.items as Array<T>
		return state.items.slice(0, getVisibleAmount()) as Array<T>
	}
	return {
		items: getItems(),
		hasMore: state.hasMore || getVisibleAmount() < state.items.length,
		loading: state.loading,
		loadMore: () => {
			if (!state.hasMore) {
				dispatch({type: 'addVisible', page: state.page + 1})
				return Promise.resolve()
			}
			return load(state.page + 1)
		}
	}
}
